import React, { useEffect, useMemo, useReducer, useState } from 'react';

import moment from 'moment';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { twMerge } from 'tailwind-merge';
import { Loader, X, ShoppingBag } from 'react-feather';
import { useDropzone } from 'react-dropzone';
import {
  Briefcase,
  CheckCircle,
  FileCsv,
  Notebook,
  Notepad,
  Warning,
  XCircle,
  CaretLeft,
  CaretRight,
  BookOpen,
  AddressBook,
} from 'phosphor-react';

import { Modal } from '@mui/material';
import {
  formatMoney,
  ManualUploadCustomer,
  ManualUploadInvoice,
  ManualUploadJob,
  ManualUploadLineItems,
  ManualUploadPayment,
  ManualUploadOpenItem,
  processAllUploadedData,
  processCombineData,
  processAllAbleUploadedData,
  processAbleCombineData,
  makeChunks,
  isDataCorrupt,
  LienOnlyUploadCustomer,
  LienOnlyUploadInvoice,
  LienOnlyUploadJob,
  processLienOnlyUploadedData,
  processP21ManualUploadUploadData,
  P21ManualUploadInvoice,
  P21ManualUploadLineItem,
  P21ManualUploadCustomer,
  processCombineP21ManualUploadData,
  P21ManualUploadOrder,
  processDDIManualUploadUploadData,
  ddiManualUploadInvoice,
  ddiManualUploadLineItem,
  ddiManualUploadCustomer,
  processCombineDDIManualUploadData,
  processMarksManualUploadUploadData,
  isMarksSupplierIncludingCustomersWithoutEmail,
  processCombineMarksManualUploadData,
  marksManualUploadInvoice,
  marksManualUploadLineItem,
  marksManualUploadCustomer,
  marksManualUploadPayment,
  processAllEclipseUploadedData,
  EclipseCustomerRecord,
  EclipseCashReceiptRecord,
  EclipseInvoiceRecord,
  processCombineEclipseManualUploadData,
  processAllEagleUploadedData,
  EagleCustomerRecord,
  EagleARRecord,
  EagleInvoiceRecord,
  processCombineEagleManualUploadData,
} from '@dill/dill-shared';

import { COLORS } from '../../../utils/colors';
import { DillButton } from '../Dill/DillButton';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { AppVirtualizedList } from '../AppVirtualizedList/AppVirtualizedList';
import {
  getManualUploadInvoices,
  manualUploadData,
} from '../../../redux/services/manualUploadService';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { openMessageModal } from '../../../redux/globalSlices/genericSlice';
import ReactPaginate from 'react-paginate';
import {
  convertKeysToCamelCase,
  firebaseLargeFileUploadHelper,
  openDillToast,
} from '../../../utils/helpers';

interface PaymentRecord {
  paymentId: string;
  invoiceId: string;
  amountPaid: string;
  openAmount: string;
  customerId: string;
  jobId: string;
  recType: string;
  appliesTo: string;
  onlinePmt: string;
  invoiceIdSeqNo: string;
  docDate: Date | null;
  ref: string;
}

const baseStyle = {
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};
export const AppManualImportCSVModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<'UPLOADING' | 'PROCESSING' | 'WAITING FILE' | 'VIEW_DATA'>(
    'WAITING FILE'
  );
  const [invoices, setInvoices] = useState<ManualUploadInvoice[]>([]);
  const [lineItemsList, setLineItemsList] = useState<ManualUploadLineItems[]>([]);
  const [jobList, setJobList] = useState<ManualUploadJob[]>([]);
  const [debitMemoList, setDebitMemoList] = useState<ManualUploadOpenItem[]>([]);
  const [financeChargeList, setFinanceChargeList] = useState<ManualUploadOpenItem[]>([]);
  const [payments, setPayments] = useState<ManualUploadPayment[]>([]);
  const [creditMemos, setCreditMemos] = useState<ManualUploadPayment[]>([]);
  const [openItemsList, setOpenItemsList] = useState<ManualUploadPayment[]>([]);
  const [invoicesOnPayment, setInvoicesOnPayments] = useState<ManualUploadPayment[]>([]);
  const [customerList, setCustomerList] = useState<ManualUploadCustomer[]>([]);
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [triggerNavigationToInvoices, setTriggerNavigationToInvoices] = useState<any>(null);
  const [fileErrors, setFileErrors] = useState<{ errorMessage: string; fileName: string }[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<
    { fileName: string; fileType: string; file: File; mimeType: string }[]
  >([]);
  const [missingFiles, setMissingFiles] = useState<string[]>([]);
  const [curentFileUploaded, setCurentFileUploaded] = useState<{ name: string; size: number }>();
  const [fileNumber, setFileNumber] = useState(0);
  const [count, setCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedTab, setSelectedTab] = useState<
    | 'invoice'
    | 'job'
    | 'lineItems'
    | 'customer'
    | 'payments'
    | 'openItems'
    | 'customerLienOnly'
    | 'jobLienOnly'
    | 'invoiceLienOnly'
    | 'p21ManualInvoices'
    | 'p21ManualLineItems'
    | 'p21ManualCustomers'
    | 'p21ManualOrders'
    | 'ddiManualUploadInvoices'
    | 'ddiManualUploadLineItems'
    | 'ddiManualUploadCustomers'
    | 'eclipseManualInvoices'
    | 'eclipseManualCustomers'
    | 'eclipseManualOrders'
    | 'eagleManualInvoices'
    | 'eagleManualCustomers'
    | 'eagleManualArDocuments'
  >('invoice');
  const [loading, setLoading] = useState(false);
  const [refreshScroll, setRefreshScroll] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  // Pagination
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 50; // Number of items per page
  const endOffset = itemOffset + itemsPerPage;
  const [currentPage, setCurrentPage] = useState(1);
  const [customerLienOnlyList, setCustomerLienOnlyList] = useState<LienOnlyUploadCustomer[]>([]);
  const [invoicesLienOnly, setInvoicesLienOnly] = useState<LienOnlyUploadInvoice[]>([]);
  const [jobLienOnlyList, setJobLienOnlyList] = useState<LienOnlyUploadJob[]>([]);
  const [customerLienOnlyListError, setCustomerLienOnlyListError] = useState<
    LienOnlyUploadCustomer[]
  >([]);
  const [invoicesLienOnlyError, setInvoicesLienOnlyError] = useState<LienOnlyUploadInvoice[]>([]);
  const [jobLienOnlyListError, setJobLienOnlyListError] = useState<LienOnlyUploadJob[]>([]);
  const [p21ManualInvoices, setP21ManualInvoices] = useState<P21ManualUploadInvoice[]>([]);
  const [p21ManualLineItemsList, setP21ManualLineItemsList] = useState<P21ManualUploadLineItem[]>(
    []
  );
  const [p21ManualCustomerList, setP21ManualCustomerList] = useState<P21ManualUploadCustomer[]>([]);
  const [p21ManualOrderList, setP21ManualOrderList] = useState<P21ManualUploadOrder[]>([]);
  const [hasInvoiceProperties, setHasInvoiceProperties] = useState(false);
  const [ddiManualUploadInvoicesList, setDDIManualUploadInvoicesList] = useState<
    ddiManualUploadInvoice[]
  >([]);
  const [ddiManualUploadLineItemsList, setDDIManualUploadLineItemsList] = useState<
    ddiManualUploadLineItem[]
  >([]);
  const [ddiManualUploadCustomerList, setDDIManualUploadCustomerList] = useState<
    ddiManualUploadCustomer[]
  >([]);
  const [marksManualUploadInvoicesList, setMarksManualUploadInvoicesList] = useState<
    marksManualUploadInvoice[]
  >([]);
  const [marksManualUploadLineItemsList, setMarksManualUploadLineItemsList] = useState<
    marksManualUploadLineItem[]
  >([]);
  const [marksManualUploadCustomerList, setMarksManualUploadCustomerList] = useState<
    marksManualUploadCustomer[]
  >([]);
  const [marksManualUploadPaymentsList, setMarksManualUploadPaymentsList] = useState<
    marksManualUploadPayment[]
  >([]);
  const [eclipseManualInvoicesList, setEclipseManualInvoicesList] = useState<
    EclipseInvoiceRecord[]
  >([]);
  const [eclipseManualCustomersList, setEclipseManualCustomersList] = useState<
    EclipseCustomerRecord[]
  >([]);
  const [eclipseManualOrdersList, setEclipseManualOrdersList] = useState<
    EclipseCashReceiptRecord[]
  >([]);
  const [eagleManualInvoicesList, setEagleManualInvoicesList] = useState<EagleInvoiceRecord[]>([]);
  const [eagleManualCustomersList, setEagleManualCustomersList] = useState<EagleCustomerRecord[]>(
    []
  );
  const [eagleManualArDocumentsList, setEagleManualArDocumentsList] = useState<EagleARRecord[]>([]);

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const currentMonth = moment().format('MMM-YYYY');
  const folderName = `manualUploadJsonData/${currentMonth}/${
    currentSupplier?.id ?? ''
  }/${new Date().getTime()}`;
  const folderExcelName = `manualUploadExcelData/${currentMonth}/${
    currentSupplier?.id ?? ''
  }/${new Date().getTime()}`;

  const [generalCheck, generalCheckDispatch] = useReducer(
    (
      state: {
        isSomeInvoicesAlreadySaved: boolean;
        isSomeInvoicesHaveDillPaymentSaved: boolean;
        isSomePaymentsAlreadySaved: boolean;
      },
      action:
        | { type: 'isSomeInvoicesAlreadySaved'; payload: boolean }
        | { type: 'isSomeInvoicesHaveDillPaymentSaved'; payload: boolean }
        | { type: 'isSomePaymentsAlreadySaved'; payload: boolean }
        | { type: 'reset' }
    ): any => {
      switch (action.type) {
        case 'isSomeInvoicesAlreadySaved':
          return { ...state, isSomeInvoicesAlreadySaved: action.payload };
        case 'isSomeInvoicesHaveDillPaymentSaved':
          return { ...state, isSomeInvoicesHaveDillPaymentSaved: action.payload };
        case 'isSomePaymentsAlreadySaved':
          return { ...state, isSomePaymentsAlreadySaved: action.payload };
        case 'reset':
          return {
            isSomeInvoicesAlreadySaved: false,
            isSomeInvoicesHaveDillPaymentSaved: false,
            isSomePaymentsAlreadySaved: false,
          };
        default:
      }
    },
    {
      isSomeInvoicesAlreadySaved: false,
      isSomeInvoicesHaveDillPaymentSaved: false,
      isSomePaymentsAlreadySaved: false,
    }
  );

  useEffect(() => {
    if (open) {
      setUploadedFiles([]);
      setOpenItemsList([]);
      setFinanceChargeList([]);
      setDebitMemoList([]);
      setCustomerList([]);
      setJobList([]);
      generalCheckDispatch({ type: 'reset' });
      setCurentFileUploaded({} as { name: string; size: number });
      setUploadProgress(0);
      setFileNumber(0);
      setCount(0);
      setMode('WAITING FILE');
      setFileErrors([]);
      setMissingFiles([]);
      setCustomerIds([]);
      setJobLienOnlyList([]);
      setCustomerLienOnlyList([]);
      setInvoicesLienOnly([]);
      setJobLienOnlyListError([]);
      setCustomerLienOnlyListError([]);
      setInvoicesLienOnlyError([]);
      setP21ManualInvoices([]);
      setP21ManualLineItemsList([]);
      setP21ManualCustomerList([]);
      setP21ManualOrderList([]);
      setHasInvoiceProperties(false);
      setDDIManualUploadInvoicesList([]);
      setDDIManualUploadLineItemsList([]);
      setDDIManualUploadCustomerList([]);
      setMarksManualUploadInvoicesList([]);
      setMarksManualUploadLineItemsList([]);
      setMarksManualUploadCustomerList([]);
      setMarksManualUploadPaymentsList([]);
    }

    return () => {};
  }, [open]);
  const getTabName = (tabName: string) => {
    let tab = '';
    if (tabName.toLowerCase().includes('invoice')) {
      tab = 'Invoice';
    } else if (tabName.toLowerCase().includes('job')) {
      tab = 'Job';
    } else if (tabName.toLowerCase().includes('lineitems')) {
      tab = 'Line Item';
    } else if (tabName.toLowerCase().includes('customer')) {
      tab = 'Customer';
    } else if (tabName.toLowerCase().includes('payment')) {
      tab = 'Payment';
    } else if (tabName.toLowerCase().includes('openitem')) {
      tab = 'Open Item';
    } else if (tabName.toLowerCase().includes('order')) {
      tab = 'Order';
    }
    return tab;
  };

  const columns = useMemo(() => {
    const currentColumns = {
      invoiceColumns: [
        { id: 'invoiceNumber', name: 'Invoice Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDate', name: 'Invoice Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'poNumber', name: 'P O Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'amount', name: 'Amount', width: 120, cellType: 'VIEW_TEXT' },
        // { id: 'balance', name: 'Balance', width: 120, cellType: 'VIEW_TEXT' },
      ],
      lineItemsColumns: [
        { id: 'invoiceId', name: 'Invoice Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'description', name: 'Description', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'unitAmount', name: 'Unit Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'subTotal', name: 'Sub Total', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'totalAmount', name: 'Total Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'taxAmount', name: 'Tax Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'discountAmount', name: 'Discount Amount', width: 120, cellType: 'VIEW_TEXT' },
      ],
      paymentsColumns: [
        { id: 'paymentId', name: 'Payment Id', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceId', name: 'Invoice Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'jobId', name: 'Job Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'amountPaid', name: 'Amount Paid', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'appliesTo', name: 'Applies To', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'docDate', name: 'Doc Date', width: 120, cellType: 'VIEW_TEXT' },
      ],
      jobColumns: [
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Name', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'address', name: 'Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zip', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      customerColumns: [
        { id: 'name', name: 'Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'emails', name: 'Emails', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'address', name: 'Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zip', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      openItemsColumns: [
        { id: 'paymentId', name: 'Payment Id', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceId', name: 'Invoice Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'amountPaid', name: 'Amount Paid', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'jobId', name: 'Job Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'docDate', name: 'Doc Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'appliesTo', name: 'Applies To', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'recType', name: 'Record Type', width: 120, cellType: 'VIEW_TEXT' },
      ],
      invoiceLienOnlyColumns: [
        { id: 'invoiceNumber', name: 'Invoice Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDate', name: 'Invoice Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'shipDate', name: 'Ship Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'dueDate', name: 'Due Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'amount', name: 'Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'balance', name: 'Balance', width: 120, cellType: 'VIEW_TEXT' },
      ],
      jobLienOnlyColumns: [
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Name', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'address', name: 'Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zip', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      customerLienOnlyColumns: [
        { id: 'name', name: 'Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'emails', name: 'Emails', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'address', name: 'Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zip', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      p21ManualInvoiceColumns: [
        { id: 'invoiceNo', name: 'Invoice No', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'customerId', name: 'Customer Id', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDate', name: 'Invoice Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'dueDate', name: 'Due Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'amountRemaining', name: 'Amount Remaining', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'totalAmount', name: 'Total Amount', width: 120, cellType: 'VIEW_TEXT' },
      ],
      p21ManualLineItemsColumns: [
        { id: 'invoiceNo', name: 'Invoice No', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'itemId', name: 'Item Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'itemDesc', name: 'Item Desc', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'qtyShipped', name: 'Qty Shipped', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'unitOfMeasure', name: 'Unit Of Measure', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'unitPrice', name: 'Unit Price', width: 120, cellType: 'VIEW_TEXT' },
      ],
      p21ManualCustomerColumns: [
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Name', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'emailAddress', name: 'Email Address', width: 180, cellType: 'VIEW_TEXT' },
      ],
      p21ManualOrderColumns: [
        { id: 'orderNo', name: 'Order Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'orderDate', name: 'Order Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'orderPoNo', name: 'Order Po No', width: 180, cellType: 'VIEW_TEXT' },
      ],
      ddiManualUploadInvoiceColumns: [
        { id: 'invoiceNumber', name: 'Invoice Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerNumber', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDate', name: 'Invoice Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDueDate', name: 'Due Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'totalInvoiceAmount', name: 'Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'invoiceBalance', name: 'Balance', width: 120, cellType: 'VIEW_TEXT' },
      ],
      ddiManualUploadLineItemsColumns: [
        { id: 'invoiceNumber', name: 'Invoice Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'productNumber', name: 'Product Number', width: 120, cellType: 'VIEW_TEXT' },
        {
          id: 'productDescription',
          name: 'Product Description',
          width: 180,
          cellType: 'VIEW_TEXT',
        },
        {
          id: 'productQuantityOrdered',
          name: 'Product Quantity Ordered',
          width: 180,
          cellType: 'VIEW_TEXT',
        },
        {
          id: 'productQuantityShipped',
          name: 'Product Quantity Shipped',
          width: 180,
          cellType: 'VIEW_TEXT',
        },
        { id: 'productUnitPrice', name: 'Product Unit Price', width: 120, cellType: 'VIEW_TEXT' },
      ],
      ddiManualUploadCustomerColumns: [
        { id: 'customerNumber', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerName', name: 'Customer Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'emails', name: 'Emails', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'addressLine1', name: 'Address Line 1', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zipCode', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      eclipseManualInvoiceColumns: [
        { id: 'orderNumber', name: 'Order Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'orderDate', name: 'Order Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'dueDate', name: 'Due Date', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'customerPO', name: 'P O Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'totalSalesAmt', name: 'Total Amount', width: 120, cellType: 'VIEW_TEXT' },
      ],
      eclipseManualCustomerColumns: [
        { id: 'customerID', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Name', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'emailAddresses', name: 'Email Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zipCode', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      eclipseManualOrderColumns: [
        { id: 'invoice', name: 'Invoice Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerID', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'appliedOrderCashAmt', name: 'Applied Cash Amt', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'cashAmount', name: 'Cash Amount', width: 120, cellType: 'VIEW_TEXT' },
      ],
      eagleManualInvoiceColumns: [
        { id: 'journalNumber', name: 'Journal Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'invoiceNumber', name: 'Invoice Number', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'date', name: 'Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'dueDate', name: 'Due Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'poNumber', name: 'P O Number', width: 180, cellType: 'VIEW_TEXT' },
      ],
      eagleManualCustomerColumns: [
        { id: 'customerNumber', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerName', name: 'Customer Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'emailAddresses', name: 'Email Addresses', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zipCode', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
      ],
      eagleManualArDocumentColumns: [
        { id: 'arDocument', name: 'A/R Document', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerNumber', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'applyToNumber', name: 'Apply To Number(s)', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'trxTypeDescription', name: 'Trx Type', width: 120, cellType: 'VIEW_TEXT' },
      ],
      marksManualUploadInvoiceColumns: [
        { id: 'invoice', name: 'Invoice Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerID', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'invoiceDate', name: 'Invoice Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'dueDate', name: 'Due Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'totalInvoice', name: 'Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'balanceDue', name: 'Balance', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'discountAmount', name: 'Discount Amount', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'discountDate', name: 'Discount Date', width: 120, cellType: 'VIEW_TEXT' },
      ],
      marksManualUploadLineItemsColumns: [
        { id: 'invoice', name: 'Invoice Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'item', name: 'Item', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'description', name: 'Description', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'qtyOrd', name: 'Qty Ordered', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'qtyShip', name: 'Qty Shipped', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'unitPrice', name: 'Unit Price', width: 120, cellType: 'VIEW_TEXT' },
      ],
      marksManualUploadCustomerColumns: [
        { id: 'customerId', name: 'Customer Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'name', name: 'Customer Name', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'emailAddress', name: 'Email Address', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'billAddress1', name: 'Address Line 1', width: 180, cellType: 'VIEW_TEXT' },
        { id: 'city', name: 'City', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'state', name: 'State', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'zip', name: 'Zip Code', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'terms', name: 'Terms', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'creditLimit', name: 'Credit Limit', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'statementFlag', name: 'STMT Flag', width: 100, cellType: 'VIEW_TEXT' },
      ],
      marksManualUploadPaymentsColumns: [
        { id: 'id', name: 'Receipt Id', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'customerID', name: 'Customer Number', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'checkNumber', name: 'Check #', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'paymentDate', name: 'Date', width: 120, cellType: 'VIEW_TEXT' },
        { id: 'type', name: 'Type', width: 100, cellType: 'VIEW_TEXT' },
        { id: 'totalAppliedAmount', name: 'Amount', width: 120, cellType: 'VIEW_TEXT' },
      ],
    };
    return currentColumns;
  }, [open]);

  const containsRequiredProperties = (data: string[], type: string) => {
    let fieldsToFind: string[] = [];
    const lowerCaseArray = data.map((str) => str?.toLowerCase());
    if (currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD') {
      if (type === 'invoice') {
        fieldsToFind = [
          'invoiceDate',
          'invoiceNumber',
          'shipDate',
          'amount',
          'balance',
          'customerId',
          'jobId',
          'dueDate',
        ];
      }
      if (type === 'customer') {
        fieldsToFind = ['name', 'id'];
      }
      if (type === 'job') {
        fieldsToFind = ['jobId', 'name', 'customerId'];
      }
    } else if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
      if (type === 'invoice') {
        fieldsToFind = [
          'Invoice No',
          'Invoice Date',
          'Customer Id',
          'Terms Desc',
          'Order Date',
          'Taker',
          'Order No',
          'Salesrep Name',
          'Amount Remaining',
          'Invoice Type',
          'Tax Amount',
          'Total Amount',
        ];
      }
      if (type === 'customer') {
        fieldsToFind = ['Customer Id', 'Name', 'Email Address'];
      }
      if (type === 'lineItems') {
        fieldsToFind = [
          'Invoice No',
          'Item Id',
          'Item Desc',
          'Qty Requested',
          'Qty Shipped',
          'Unit Of Measure',
          'Unit Price',
        ];
      }
      if (type === 'orders') {
        fieldsToFind = ['Order No', 'Order Date', 'Order Po No'];
      }
    } else if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
      if (type === 'invoice') {
        fieldsToFind = [
          'Customer Number',
          'Invoice Balance',
          'Invoice Date',
          'Invoice Due Date',
          'Invoice Last Payment Date',
          'Invoice Number',
          'Invoice Ship Date',
          'Invoice Subtotal Amount',
          'Invoice Tax Amount',
          'Invoice Terms Description',
          'Customer PO Number',
          'Invoice ShipTo Name',
          'Invoice ShipTo Attention',
          'Invoice ShipTo Address',
          'Invoice ShipTo Address Line 2',
          'Invoice ShipTo City',
          'Invoice ShipTo State',
          'Invoice ShipTo Zip',
          'Invoice Job ID',
          'Invoice Ship Via Description',
          'Invoice Salesperson Code',
          'Freight Amount',
          'Invoice Merchandise Amount',
          'Invoice Misc Charge Amount',
          'Invoice Taxable Amount',
          'Invoice Order Writer',
          'Invoice Line Extension',
          'Product Line Number',
          'Product Ordered Quantity',
          'Product Quantity Shipped',
          'Product Description',
          'Product Number',
          'Product Unit Price',
        ];
      }
      if (type === 'customer') {
        fieldsToFind = [
          'Customer Number',
          'Customer Name',
          'Email Address',
          'Primary Email Address',
          'Address Line1',
          'Address Line2',
          'Address Line3',
          'City',
          'State',
          'Zip Code',
        ];
      }
      if (type === 'openItems') {
        fieldsToFind = [
          'Customer Number',
          'Invoice Original Amount',
          'Invoice Balance',
          'Invoice Date',
          'Invoice Number',
          'Customer PO Number',
          'Sales Order Date',
        ];
      }
    } else if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
      if (type === 'invoice') {
        // Some Marks suppliers (e.g. Northwest Pipe) export invoices with a different column
        // layout than the original Marks format (no "Customer ID"/"Customer PO" columns - instead
        // "Cons#" and "Cust-PO#"). Accept either header shape so both keep working.
        const marksInvoiceHeaderVariants = [
          ['Invoice#', 'Customer ID', 'Invoice Date', 'Customer PO', 'Total Invoice'],
          ['Invoice#', 'Cons#', 'Invoice Date', 'Cust-PO#', 'Total Invoice'],
        ];
        return marksInvoiceHeaderVariants.some((variantFields) =>
          variantFields.every((field) => lowerCaseArray.includes(field.toLowerCase()))
        );
      }
      if (type === 'lineItems') {
        fieldsToFind = ['Invoice#', 'Line number', 'Item#', 'Description', 'Extended Price'];
      }
      if (type === 'payments') {
        // Northwest Pipe payments made outside of Dill (Dill payments are filtered out by them).
        fieldsToFind = ['Id', 'Cons#', 'Invoice#', 'Date', 'Check#', 'Applied Amount'];
      }
      if (type === 'customer') {
        // Northwest Pipe's customer file has no "Customer Id" column - it uses "Cons#" instead.
        const marksCustomerHeaderVariants = [
          ['Customer Id', 'Name', 'Email Address'],
          ['Cons#', 'Name', 'Email Address'],
        ];
        return marksCustomerHeaderVariants.some((variantFields) =>
          variantFields.every((field) => lowerCaseArray.includes(field.toLowerCase()))
        );
      }
    } else if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
      if (type === 'customer') {
        fieldsToFind = [
          'Bill To ID',
          'Bill-To?',
          'Home Branch',
          'Customer ID',
          'Customer Name',
          'Address',
          'City',
          'State',
          'Zip Code',
          'Email Addresses',
        ];
      }
      if (type === 'cashReceiptList') {
        fieldsToFind = [
          'Customer ID',
          'Cash Receipt ID',
          'Invoice #',
          'Cash Amount',
          'Applied Order Cash Amt',
          'Discount Given',
          'G/L Date',
          'Unapplied Amount',
        ];
      }
      if (type === 'invoice') {
        fieldsToFind = [
          'Ship Customer ID',
          'Bill Customer ID',
          'Order Status',
          'Product ID',
          'Description',
          'Ship Qty Per',
          'Order Qty Per',
          'Unit Price',
          'Unit Price Extended',
          'Order Number',
        ];
      }
    } else if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
      if (type === 'customer') {
        fieldsToFind = [
          'Address 1',
          'Address 2',
          'City',
          'State',
          'Zip Code',
          'Customer Terms Code',
          'Customer Terms Description',
          'Credit Limit',
          'Email Address',
          'Customer Number',
          'Customer Name',
          'Store Number',
          'Store Short Name',
        ];
      }
      if (type === 'invoice') {
        fieldsToFind = [
          'Customer Name',
          'Customer Address 1',
          'Customer Address 2',
          'Customer Address 3',
          'Invoice Number',
          // 'Journal Number',
          'Order Number',
          'PO #',
          'Reference',
          'Salesperson Number',
          'Salesperson Name',
          'Ship To Name',
          'Ship To Address 1',
          'Ship To Address 2',
          'Ship To City',
          'Ship To State',
          'Ship To Zip 5',
          'Line #',
          'Line Item Description',
          'Price UM',
          'Customer Number',
          'Job',
          'Terms',
          'Clerk',
          'Date',
          'Due Date',
          'Terminal',
          'Sequence Number',
          'Actual Price',
          'Measure',
          'Value',
          'Customer Tax Code',
          'Customer Tax Location',
          'Store Number',
          'Store Short Name',
        ];
      }
      if (type === 'arDocument') {
        fieldsToFind = [
          'A/R Document',
          'Apply To Number',
          'Check No',
          'Trx Type ID',
          'Trx Type Description',
          'Sales Tax',
          'Net Total Amount',
          'Terms Discount',
          'Taxable Merchandise',
          'Customer Number',
          'Customer Name',
          'Date',
        ];
      }
    } else {
      if (type === 'invoice') {
        fieldsToFind = [
          'customerId',
          'invoiceNumber',
          'invoiceDate',
          'dueDate',
          'amount',
          'balance',
        ];
      }
      if (type === 'customer') {
        fieldsToFind = ['name', 'emails'];
      }
      if (type === 'lineItems') {
        fieldsToFind = ['invoiceId', 'name', 'unitAmount', 'quantity', 'subTotal', 'totalAmount'];
      }
      if (type === 'job') {
        fieldsToFind = ['customerId', 'name'];
      }

      if (type === 'payments') {
        fieldsToFind = [
          'paymentId',
          'invoiceId',
          'amountPaid',
          'openAmount',
          'customerId',
          'jobId',
        ];
      }

      if (type === 'ar_balance') {
        fieldsToFind = [
          'customer',
          'name',
          'address',
          'opened',
          'reference #',
          'doc',
          'entry date',
          'due date',
          'orig amount',
          'amount',
          'tc',
          'reg#',
          'disc amt',
          'c',
          'dispute memo',
          'so doc',
        ];
      }

      if (type === 'invoice_header') {
        fieldsToFind = [
          'wh',
          'customer',
          'name',
          'inv',
          'doc',
          'inv date',
          'taxable',
          'misc amt',
          'merch amt',
          'tax',
          'freight',
          'ship via',
          'reference#',
          'slsp 2',
        ];
      }

      if (type === 'invoice_details') {
        fieldsToFind = [
          'iw',
          'sw',
          'inv',
          'doc',
          'item',
          'description',
          'customer',
          'slsp 2',
          'inv date',
          'price',
          'shipped',
          'sx',
        ];
      }

      if (type === 'billing_customer') {
        fieldsToFind = ['customer', 'name', 'address', 'city', 'st', 'zip', 'email 1', 'email 2'];
      }
    }

    const missingFields = fieldsToFind.filter(
      (field) => !lowerCaseArray.includes(field?.toLowerCase())
    );

    if (missingFields.length === 0) {
      return true;
    }
    return false;
  };

  const jobContainsInvoiceProperties = (data: string[]) => {
    const lowerCaseArray = data.map((str) => str?.toLowerCase());
    const includeInvoiceProperties =
      lowerCaseArray.includes('invoicedate') ||
      lowerCaseArray.includes('invoicenumber') ||
      lowerCaseArray.includes('shipdate') ||
      lowerCaseArray.includes('duedate') ||
      lowerCaseArray.includes('amount') ||
      lowerCaseArray.includes('balance');

    setHasInvoiceProperties(includeInvoiceProperties);
  };

  const checkErrors = () => {
    let requiredFileTypes: string[] = [];
    if (
      currentSupplier?.integrationType !== 'LIEN_ONLY_UPLOAD' &&
      currentSupplier?.integrationType !== 'P21_MANUAL_UPLOAD' &&
      currentSupplier?.integrationType !== 'DDI_MANUAL_UPLOAD' &&
      currentSupplier?.integrationType !== 'MARKS_MANUAL_UPLOAD' &&
      currentSupplier?.integrationType !== 'ECLIPSE_MANUAL_UPLOAD' &&
      currentSupplier?.integrationType !== 'EAGLE_MANUAL_UPLOAD'
    ) {
      if (
        uploadedFiles.some((uploadedFile) => {
          return uploadedFile.fileType === 'INVOICE' || uploadedFile.fileType === 'LINE_ITEMS';
        })
      ) {
        requiredFileTypes = ['INVOICE', 'LINE_ITEMS'];
      }
      if (
        uploadedFiles.some((uploadedFile) => {
          return (
            uploadedFile.fileType === 'INVOICE_DETAILS' ||
            uploadedFile.fileType === 'INVOICE_HEADER' ||
            uploadedFile.fileType === 'AR_BALANCE'
          );
        })
      ) {
        requiredFileTypes = ['INVOICE_DETAILS', 'INVOICE_HEADER', 'AR_BALANCE'];
      }
    }
    if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
      if (
        uploadedFiles.some((uploadedFile) => {
          return (
            uploadedFile.fileType === 'INVOICE' ||
            uploadedFile.fileType === 'LINE_ITEMS' ||
            uploadedFile.fileType === 'ORDERS'
          );
        })
      ) {
        requiredFileTypes = ['INVOICE', 'LINE_ITEMS', 'ORDERS'];
      }
    }
    if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
      if (
        uploadedFiles.some((uploadedFile) => {
          return uploadedFile.fileType === 'INVOICE' || uploadedFile.fileType === 'OPEN_ITEMS';
        })
      ) {
        requiredFileTypes = ['INVOICE', 'OPEN_ITEMS'];
      }
    }
    if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
      if (
        uploadedFiles.some((uploadedFile) => {
          return (
            uploadedFile.fileType === 'INVOICE' ||
            uploadedFile.fileType === 'LINE_ITEMS' ||
            uploadedFile.fileType === 'CUSTOMER'
          );
        })
      ) {
        requiredFileTypes = ['INVOICE', 'LINE_ITEMS', 'CUSTOMER'];
      }
    }
    if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
      if (
        uploadedFiles.some((uploadedFile) => {
          return (
            uploadedFile.fileType === 'INVOICE' || uploadedFile.fileType === 'CASH_RECEIPT_LIST'
          );
        })
      ) {
        requiredFileTypes = ['INVOICE', 'CASH_RECEIPT_LIST'];
      }
    }
    if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
      if (
        uploadedFiles.some((uploadedFile) => {
          return (
            uploadedFile.fileType === 'INVOICE' ||
            uploadedFile.fileType === 'CUSTOMER' ||
            uploadedFile.fileType === 'AR_DOCUMENT'
          );
        })
      ) {
        requiredFileTypes = ['INVOICE', 'CUSTOMER', 'AR_DOCUMENT'];
      }
    }

    const errors = requiredFileTypes
      .filter((type) => !uploadedFiles.some((file) => file.fileType === type))
      .map((type) => type.replaceAll('_', ' ')?.toLowerCase());
    setMissingFiles(errors);
    return errors;
  };

  useEffect(() => {
    setCount(count + 1);
  }, [curentFileUploaded]);

  const cleanUpHeaders = (headers: string[]) => {
    return headers.map((header: string) => {
      return header.replaceAll('ï»¿', '');
    });
  };

  const onDrop = async (acceptedFiles: File[], fileRejections: any[]) => {
    try {
      setFileErrors([]);
      setMissingFiles([]);
      setFileNumber(acceptedFiles.length);
      if (acceptedFiles) {
        setError('');
        setMode('UPLOADING');
        const processedFiles = [...uploadedFiles];
        const filesToAdd: { fileName: string; fileType: string; file: File; mimeType: string }[] =
          [];

        const readFileAsBinaryString = (file: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onprogress = (event: ProgressEvent<FileReader>) => {
              if (event.lengthComputable) {
                const percentCompleted = Math.round((event.loaded * 100) / event.total);
                setUploadProgress(percentCompleted);
              }
            };
            reader.onload = (event: ProgressEvent<FileReader>) => {
              if (event.target && event.target.result) {
                resolve(event.target.result.toString());
              } else {
                reject(new Error('File reading failed'));
              }
            };
            reader.onerror = () => {
              reject(new Error('File reading failed'));
            };
            reader.readAsBinaryString(file);
          });
        };
        const fileNames: string[] = [];
        for (const file of acceptedFiles) {
          try {
            setMode('UPLOADING');
            const result = await readFileAsBinaryString(file);
            fileNames.push(file.name);
            setCurentFileUploaded({ name: file.name, size: file.size });
            if (file.name.toLowerCase().endsWith('.csv')) {
              Papa.parse(result, {
                complete: async (parsedData) => {
                  const fields = parsedData.meta.fields;
                  const header = cleanUpHeaders(fields as string[]);
                  if (currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD') {
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifJobFile = containsRequiredProperties(header as string[], 'job');
                    const mimeType = file.type;
                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifJobFile) {
                      jobContainsInvoiceProperties(header as string[]);
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'JOB',
                        file,
                        mimeType: mimeType,
                      });
                    }
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });
                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'ABLE_MANUAL_UPLOAD') {
                    const checkIfARBalanceFile = containsRequiredProperties(
                      header as string[],
                      'ar_balance'
                    );
                    const checkIfInvoiceBillingFile = containsRequiredProperties(
                      header as string[],
                      'invoice_details'
                    );
                    const checkIfInvoiceHeaderFile = containsRequiredProperties(
                      header as string[],
                      'invoice_header'
                    );
                    const checkIfBillingCustomerFile = containsRequiredProperties(
                      header as string[],
                      'billing_customer'
                    );

                    const mimeType = file.type;
                    if (checkIfARBalanceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'AR_BALANCE',
                        file,
                        mimeType,
                      });
                    if (checkIfInvoiceHeaderFile) {
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE_HEADER',
                        file,
                        mimeType,
                      });
                    }
                    if (checkIfInvoiceBillingFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE_DETAILS',
                        file,
                        mimeType,
                      });
                    if (checkIfBillingCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'BILLING_CUSTOMER',
                        file,
                        mimeType,
                      });

                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });
                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifLineItemsFile = containsRequiredProperties(
                      header as string[],
                      'lineItems'
                    );
                    const checkIifOrderFile = containsRequiredProperties(
                      header as string[],
                      'orders'
                    );

                    const mimeType = file.type;
                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifLineItemsFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'LINE_ITEMS',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifOrderFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'ORDERS',
                        file,
                        mimeType: mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });

                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifOpenItemsFile = containsRequiredProperties(
                      header as string[],
                      'openItems'
                    );
                    const mimeType = file.type;
                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifOpenItemsFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'OPEN_ITEMS',
                        file,
                        mimeType: mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });
                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
                    console.log(header);
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifLineItemsFile = containsRequiredProperties(
                      header as string[],
                      'lineItems'
                    );
                    const mimeType = file.type;
                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifLineItemsFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'LINE_ITEMS',
                        file,
                        mimeType: mimeType,
                      });
                    if (containsRequiredProperties(header as string[], 'payments'))
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'PAYMENTS',
                        file,
                        mimeType: mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });
                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
                    const mimeType = file.type;
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCashReceiptListFile = containsRequiredProperties(
                      header as string[],
                      'cashReceiptList'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );

                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType,
                      });
                    if (checkIifCashReceiptListFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CASH_RECEIPT_LIST',
                        file,
                        mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) =>
                          newFile.fileName === existingFile.fileName &&
                          newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });

                    setUploadedFiles(processedFiles);
                  } else if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
                    const mimeType = file.type;
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifArDocumentFile = containsRequiredProperties(
                      header as string[],
                      'arDocument'
                    );

                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType,
                      });
                    if (checkIifArDocumentFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'AR_DOCUMENT',
                        file,
                        mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) =>
                          newFile.fileName === existingFile.fileName &&
                          newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });

                    setUploadedFiles(processedFiles);
                  } else {
                    const checkIifInvoiceFile = containsRequiredProperties(
                      header as string[],
                      'invoice'
                    );
                    const checkIifCustomerFile = containsRequiredProperties(
                      header as string[],
                      'customer'
                    );
                    const checkIifLineItemsFile = containsRequiredProperties(
                      header as string[],
                      'lineItems'
                    );
                    const checkIifJobFile = containsRequiredProperties(header as string[], 'job');
                    const checkIifPaymentsFile = containsRequiredProperties(
                      header as string[],
                      'payments'
                    );

                    const mimeType = file.type;
                    if (checkIifInvoiceFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'INVOICE',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifCustomerFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'CUSTOMER',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifLineItemsFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'LINE_ITEMS',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifJobFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'JOB',
                        file,
                        mimeType: mimeType,
                      });
                    if (checkIifPaymentsFile)
                      filesToAdd.push({
                        fileName: file.name,
                        fileType: 'PAYMENTS',
                        file,
                        mimeType: mimeType,
                      });
                    filesToAdd.forEach((newFile) => {
                      const index = processedFiles.findIndex(
                        (existingFile) => newFile.fileType === existingFile.fileType
                      );
                      if (index !== -1) {
                        processedFiles[index] = newFile;
                      } else {
                        processedFiles.push(newFile);
                      }
                    });
                    setUploadedFiles(processedFiles);
                  }
                },
                header: true,
              });
            } else if (
              file.name.toLowerCase().endsWith('.xls') ||
              file.name.toLowerCase().endsWith('.xlsx')
            ) {
              const mimeType = file.type;
              const workbook = XLSX.read(result, { type: 'binary', cellStyles: true });
              const sheetName: string = workbook.SheetNames[0]; // Assuming the data is in the first sheet
              const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
              const options = { header: 1 };
              const sheetData2 = XLSX.utils.sheet_to_json(worksheet, options);
              const header = sheetData2.shift();
              if (currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifJobFile = containsRequiredProperties(header as string[], 'job');

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifJobFile) {
                  jobContainsInvoiceProperties(header as string[]);
                  filesToAdd.push({ fileName: file.name, fileType: 'JOB', file, mimeType });
                }

                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });
                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'ABLE_MANUAL_UPLOAD') {
                const checkIfARBalanceFile = containsRequiredProperties(
                  header as string[],
                  'ar_balance'
                );
                const checkIfInvoiceHeaderFile = containsRequiredProperties(
                  header as string[],
                  'invoice_header'
                );
                const checkIfInvoiceBillingFile = containsRequiredProperties(
                  header as string[],
                  'invoice_details'
                );
                const checkIfBillingCustomerFile = containsRequiredProperties(
                  header as string[],
                  'billing_customer'
                );

                if (checkIfARBalanceFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'AR_BALANCE',
                    file,
                    mimeType,
                  });
                if (checkIfInvoiceHeaderFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'INVOICE_HEADER',
                    file,
                    mimeType,
                  });
                if (checkIfInvoiceBillingFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'INVOICE_DETAILS',
                    file,
                    mimeType,
                  });
                if (checkIfBillingCustomerFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'BILLING_CUSTOMER',
                    file,
                    mimeType,
                  });

                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });
                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifLineItemsFile = containsRequiredProperties(
                  header as string[],
                  'lineItems'
                );
                const checkIifOrderFile = containsRequiredProperties(header as string[], 'orders');

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifLineItemsFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'LINE_ITEMS',
                    file,
                    mimeType,
                  });
                if (checkIifOrderFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'ORDERS', file, mimeType });

                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });

                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifOpenItemsFile = containsRequiredProperties(
                  header as string[],
                  'openItems'
                );

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifOpenItemsFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'OPEN_ITEMS',
                    file,
                    mimeType,
                  });
                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });

                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifLineItemsFile = containsRequiredProperties(
                  header as string[],
                  'lineItems'
                );

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifLineItemsFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'LINE_ITEMS',
                    file,
                    mimeType,
                  });
                if (containsRequiredProperties(header as string[], 'payments'))
                  filesToAdd.push({ fileName: file.name, fileType: 'PAYMENTS', file, mimeType });
                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });

                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCashReceiptListFile = containsRequiredProperties(
                  header as string[],
                  'cashReceiptList'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );

                if (checkIifInvoiceFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'INVOICE',
                    file,
                    mimeType,
                  });
                if (checkIifCashReceiptListFile)
                  filesToAdd.push({
                    fileName: file.name,
                    fileType: 'CASH_RECEIPT_LIST',
                    file,
                    mimeType,
                  });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) =>
                      newFile.fileName === existingFile.fileName &&
                      newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });

                setUploadedFiles(processedFiles);
              } else if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifArDocumentFile = containsRequiredProperties(
                  header as string[],
                  'arDocument'
                );

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifArDocumentFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'AR_DOCUMENT', file, mimeType });
                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) =>
                      newFile.fileName === existingFile.fileName &&
                      newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });

                setUploadedFiles(processedFiles);
              } else {
                const checkIifInvoiceFile = containsRequiredProperties(
                  header as string[],
                  'invoice'
                );
                const checkIifCustomerFile = containsRequiredProperties(
                  header as string[],
                  'customer'
                );
                const checkIifLineItemsFile = containsRequiredProperties(
                  header as string[],
                  'lineItems'
                );
                const checkIifJobFile = containsRequiredProperties(header as string[], 'job');
                const checkIifPaymentsFile = containsRequiredProperties(
                  header as string[],
                  'payments'
                );

                if (checkIifInvoiceFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'INVOICE', file, mimeType });
                if (checkIifCustomerFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'CUSTOMER', file, mimeType });
                if (checkIifLineItemsFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'LINE_ITEMS', file, mimeType });
                if (checkIifJobFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'JOB', file, mimeType });
                if (checkIifPaymentsFile)
                  filesToAdd.push({ fileName: file.name, fileType: 'PAYMENTS', file, mimeType });

                filesToAdd.forEach((newFile) => {
                  const index = processedFiles.findIndex(
                    (existingFile) => newFile.fileType === existingFile.fileType
                  );
                  if (index !== -1) {
                    processedFiles[index] = newFile;
                  } else {
                    processedFiles.push(newFile);
                  }
                });
                setUploadedFiles(processedFiles);
              }
            }
          } catch (err) {
            console.error(`Error processing file ${file.name}:`, err);
            openDillToast({
              message: `Error processing file ${file.name}. Please try again.`,
              type: 'ERROR',
            });
            setMode('WAITING FILE');
            return;
          }
        }
        if (filesToAdd.length === 0 && fileNames.length > 0) {
          const fileReject = fileNames.map((fileName) => ({
            errorMessage:
              currentSupplier?.integrationType === 'ABLE_MANUAL_UPLOAD'
                ? 'The file is not an AR Balance file, Invoice Header file, Invoice Details file or Billing Customer file.'
                : currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD'
                ? 'The file is not an invoice file, customer file or job file.'
                : currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD'
                ? 'The file is not an invoice file, customer file, line items file or orders file.'
                : currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD'
                ? 'The file is not an invoice file, open items file or a customer file'
                : currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD'
                ? 'The file is not an invoice file, line items file, customer file or payments file.'
                : currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD'
                ? 'The file is not an customer file, invoice file or sales orders file.'
                : currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD'
                ? 'The file is not a customer file, invoice file or A/R document file.'
                : 'The file is not an invoice file, customer file, line items file, job file or payments file.',
            fileName: fileName,
          }));
          setFileErrors(fileReject);
        }
        setMode('WAITING FILE');
      }

      if (fileRejections && fileRejections.length > 0) {
        const fileReject = fileRejections.map((file) => ({
          errorMessage:
            file.errors[0]?.code === 'file-invalid-type'
              ? 'File should be either .xls, .xlsx, or .csv'
              : file.errors[0]?.message || 'File is rejected',
          fileName: file.file ? file.file.name : '',
        }));
        setFileErrors(fileReject);
      }

      setMode('WAITING FILE');
    } catch (err) {
      console.log(err);
      openDillToast({
        message: `Error processing files. Please try again.`,
        type: 'ERROR',
      });
      setMode('WAITING FILE');
    }
  };

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    open: openFilePicker,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    noClick: true,
    noKeyboard: true,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleCloseModal = () => {
    setMode('WAITING FILE');
    setError('');
    setFileErrors([]);
    setUploadedFiles([]);
    setMissingFiles([]);
    setCustomerList([]);
    setJobList([]);
    setLineItemsList([]);
    setInvoices([]);
    setPayments([]);
    setOpenItemsList([]);
    setRefreshScroll(false);
    setLoading(false);
    setInvoicesOnPayments([]);
    setCustomerIds([]);
    setJobLienOnlyList([]);
    setCustomerLienOnlyList([]);
    setInvoicesLienOnly([]);
    setUploadingData(false);
    setJobLienOnlyListError([]);
    setCustomerLienOnlyListError([]);
    setInvoicesLienOnlyError([]);
    setP21ManualCustomerList([]);
    setP21ManualLineItemsList([]);
    setP21ManualInvoices([]);
    setP21ManualOrderList([]);
    setDDIManualUploadInvoicesList([]);
    setDDIManualUploadLineItemsList([]);
    setDDIManualUploadCustomerList([]);
    setEclipseManualInvoicesList([]);
    setEclipseManualCustomersList([]);
    setEclipseManualOrdersList([]);
    setEagleManualInvoicesList([]);
    setEagleManualCustomersList([]);
    setEagleManualArDocumentsList([]);
    setHasInvoiceProperties(false);
    handleClose();
  };

  const handleProcessData = async () => {
    try {
      if (uploadedFiles.length === 0) {
        setFileErrors([{ errorMessage: 'No files selected', fileName: '' }]);
        return;
      }
      setLoading(true);
      setMode('PROCESSING');
      if (currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD') {
        const processedUploadedData = await processLienOnlyUploadedData(uploadedFiles);
        if (processedUploadedData.data) {
          setInvoicesLienOnly(
            processedUploadedData.data.invoices.filter(
              (invoice) => invoice.id !== '' && !isDataCorrupt(invoice)
            )
          );
          setInvoicesLienOnlyError(
            processedUploadedData.data.invoices.filter(
              (invoice) => invoice.id === '' || isDataCorrupt(invoice)
            )
          );
          setCustomerLienOnlyList(
            processedUploadedData.data.customers.filter(
              (customer) => customer.id !== '' && customer.name !== '' && !isDataCorrupt(customer)
            )
          );
          setCustomerLienOnlyListError(
            processedUploadedData.data.customers.filter(
              (customer) => customer.id === '' || customer.name === '' || isDataCorrupt(customer)
            )
          );
          setJobLienOnlyList(processedUploadedData.data.jobs.filter((job) => job.id !== ''));
          setJobLienOnlyListError(processedUploadedData.data.jobs.filter((job) => job.id === ''));
          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
        const processedUploadedData = await processDDIManualUploadUploadData(uploadedFiles);
        if (processedUploadedData.data) {
          setDDIManualUploadInvoicesList(processedUploadedData.data.invoices);
          setDDIManualUploadLineItemsList(processedUploadedData.data.lineItems);
          setDDIManualUploadCustomerList(processedUploadedData.data.customers);

          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
        const processedUploadedData = await processMarksManualUploadUploadData(uploadedFiles, {
          includeCustomersWithoutEmail: isMarksSupplierIncludingCustomersWithoutEmail(
            currentSupplier?.domainId
          ),
        });
        if (processedUploadedData.data) {
          setMarksManualUploadInvoicesList(processedUploadedData.data.invoices);
          setMarksManualUploadLineItemsList(processedUploadedData.data.lineItems);
          setMarksManualUploadCustomerList(processedUploadedData.data.customers);
          setMarksManualUploadPaymentsList(processedUploadedData.data.payments);

          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
        const processedUploadedData = await processP21ManualUploadUploadData(uploadedFiles);
        if (processedUploadedData.data) {
          setP21ManualCustomerList(processedUploadedData.data.customers);
          setP21ManualLineItemsList(processedUploadedData.data.lineItems);
          setP21ManualInvoices(processedUploadedData.data.invoices);
          setP21ManualOrderList(processedUploadedData.data.orders);

          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
        const processedUploadedData = await processAllEclipseUploadedData(uploadedFiles);

        if (processedUploadedData.data) {
          setEclipseManualInvoicesList(processedUploadedData.data.invoices);
          setEclipseManualCustomersList(processedUploadedData.data.customers);
          setEclipseManualOrdersList(processedUploadedData.data.cashSalesReceipts);
          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
        const processedUploadedData = await processAllEagleUploadedData(uploadedFiles);

        if (processedUploadedData.data) {
          setEagleManualInvoicesList(processedUploadedData.data.invoices);
          setEagleManualCustomersList(processedUploadedData.data.customers);
          setEagleManualArDocumentsList(processedUploadedData.data.arDocuments);
          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else if (currentSupplier?.integrationType === 'ABLE_MANUAL_UPLOAD') {
        const processedUploadedData = await processAllAbleUploadedData(uploadedFiles);
        if (processedUploadedData.data) {
          setInvoices(processedUploadedData.data.invoices);
          setLineItemsList(processedUploadedData.data.lineItems);
          setCustomerList(processedUploadedData.data.customers);
          setJobList(processedUploadedData.data.jobs);
          setPayments(processedUploadedData.data.payments);
          setCreditMemos(processedUploadedData.data.creditMemos);
          setDebitMemoList(processedUploadedData.data.debitMemos);
          setFinanceChargeList(processedUploadedData.data.financeCharges);
          setOpenItemsList(processedUploadedData.data.openItems);
          setInvoicesOnPayments(processedUploadedData.data.invoicesOnPayment);
          setCustomerIds(processedUploadedData.data.customerIds);

          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      } else {
        const processedUploadedData = await processAllUploadedData(
          uploadedFiles,
          currentSupplier?.customerIds ?? []
        );
        if (processedUploadedData.data) {
          setInvoices(processedUploadedData.data.invoices);
          setLineItemsList(processedUploadedData.data.lineItems);
          setCustomerList(processedUploadedData.data.customers);
          setJobList(processedUploadedData.data.jobs);
          setPayments(processedUploadedData.data.payments);
          setCreditMemos(processedUploadedData.data.creditMemos);
          setDebitMemoList(processedUploadedData.data.debitMemos);
          setFinanceChargeList(processedUploadedData.data.financeCharges);
          setOpenItemsList(processedUploadedData.data.openItems);
          setInvoicesOnPayments(processedUploadedData.data.invoicesOnPayment);
          setCustomerIds(processedUploadedData.data.customerIds);

          setMode('VIEW_DATA');
        } else {
          throw new Error('Error while processing the data.');
        }
      }
    } catch (err: any) {
      console.log(err);
      dispatch(
        openMessageModal({
          modalType: 'ERROR',
          title: 'Error occurred',
          subTitle: `${err.message || err}`,
          buttonText: 'Close',
        })
      );
      setMode('WAITING FILE');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerNavigationToInvoices) {
      // navigate('/invoices');
      setTriggerNavigationToInvoices(null);
    }

    return () => {};
  }, [triggerNavigationToInvoices]);

  async function uploadChunks(data: any[], fileNamePrefix: string, chunkSize?: number) {
    try {
      if (chunkSize) {
        const chunkedData = makeChunks(data, chunkSize);
        for (const [index, chunk] of chunkedData.entries()) {
          const jsonData = JSON.stringify(chunk, null, 4);
          const blob = new Blob([jsonData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          await firebaseLargeFileUploadHelper({
            blobUrl: url,
            fileNameWithExtension: `${fileNamePrefix}_${index}.json`,
            storagePath: folderName,
          });
        }
      } else {
        const jsonData = JSON.stringify(data, null, 4);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        await firebaseLargeFileUploadHelper({
          blobUrl: url,
          fileNameWithExtension: `${fileNamePrefix}.json`,
          storagePath: folderName,
        });
      }
    } catch (err: any) {
      console.log(err);
    }
  }

  const handleImportData = async () => {
    try {
      setLoading(true);
      setUploadingData(true);
      setMode('PROCESSING');
      if (currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD') {
        await uploadChunks(
          invoicesLienOnly.filter((data) => !isDataCorrupt(data)),
          'INVOICES',
          2000
        );
        await uploadChunks(
          customerLienOnlyList.filter((data) => !isDataCorrupt(data)),
          'CUSTOMERS',
          2000
        );
        await uploadChunks(
          jobLienOnlyList.filter((data) => !isDataCorrupt(data)),
          'JOBS',
          2000
        );
      } else if (currentSupplier?.integrationType === 'P21_MANUAL_UPLOAD') {
        const combineData = await processCombineP21ManualUploadData({
          invoices: p21ManualInvoices,
          lineItems: p21ManualLineItemsList,
          customers: p21ManualCustomerList,
          orders: p21ManualOrderList,
        });
        if (!combineData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combineData.invoices, 'INVOICES', 2000);
        await uploadChunks(combineData.customers, 'CUSTOMERS', 2000);
      } else if (currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD') {
        const combineData = await processCombineDDIManualUploadData({
          invoices: ddiManualUploadInvoicesList as ddiManualUploadInvoice[],
          lineItems: ddiManualUploadLineItemsList as ddiManualUploadLineItem[],
          customers: ddiManualUploadCustomerList as ddiManualUploadCustomer[],
        });
        if (!combineData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combineData.invoices, 'INVOICES', 2000);
        await uploadChunks(combineData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combineData.invoiceId, 'UPLOADED_INV_IDS');
      } else if (currentSupplier?.integrationType === 'MARKS_MANUAL_UPLOAD') {
        const combineData = await processCombineMarksManualUploadData({
          invoices: marksManualUploadInvoicesList,
          lineItems: marksManualUploadLineItemsList,
          customers: marksManualUploadCustomerList,
          payments: marksManualUploadPaymentsList,
        });
        if (!combineData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combineData.invoices, 'INVOICES', 2000);
        await uploadChunks(combineData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combineData.invoiceId, 'UPLOADED_INV_IDS');
        if (combineData.payments.length > 0) {
          await uploadChunks(combineData.payments, 'PAYMENTS', 2000);
        }
      } else if (currentSupplier?.integrationType === 'ECLIPSE_MANUAL_UPLOAD') {
        const combineData = await processCombineEclipseManualUploadData({
          invoices: eclipseManualInvoicesList,
          customers: eclipseManualCustomersList,
          cashReceipts: eclipseManualOrdersList,
        });

        if (!combineData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combineData.invoices, 'INVOICES', 2000);
        await uploadChunks(combineData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combineData.cashReceipts, 'CASH_RECEIPTS', 2000);
        await uploadChunks(combineData.invoiceIds, 'CASH_INV_IDS', 2000);
      } else if (currentSupplier?.integrationType === 'EAGLE_MANUAL_UPLOAD') {
        const combineData = await processCombineEagleManualUploadData({
          invoices: eagleManualInvoicesList,
          customers: eagleManualCustomersList,
          arDocuments: eagleManualArDocumentsList,
        });

        if (!combineData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combineData.invoices, 'INVOICES', 2000);
        await uploadChunks(combineData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combineData.arDocuments, 'AR_DOCUMENTS', 2000);
        await uploadChunks(combineData.invoiceIds, 'AR_INV_IDS', 2000);
      } else if (currentSupplier?.integrationType === 'ABLE_MANUAL_UPLOAD') {
        const combinedData = await processAbleCombineData({
          invoices,
          lineItems: lineItemsList,
          customers: customerList,
          jobs: jobList,
          payments,
          creditMemos,
          debitMemos: debitMemoList,
          financeCharges: financeChargeList,
          openItems: openItemsList,
          invoicesOnPayment,
        });
        if (!combinedData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combinedData.invoices, 'INVOICES', 2000);
        await uploadChunks(combinedData.payments, 'PAYMENTS', 2000);
        await uploadChunks(combinedData.creditMemos, 'CREDIT_MEMOS', 2000);
        await uploadChunks(combinedData.debitMemos, 'DEBIT_MEMOS', 2000);
        await uploadChunks(combinedData.financeCharges, 'FINANCE_CHARGES', 2000);
        await uploadChunks(combinedData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combinedData.jobs, 'JOBS', 2000);
        await uploadChunks(customerIds, 'PROVIDER_IDS', 2000);
      } else {
        const combinedData = await processCombineData({
          invoices,
          lineItems: lineItemsList,
          customers: customerList,
          jobs: jobList,
          payments,
          creditMemos,
          debitMemos: debitMemoList,
          financeCharges: financeChargeList,
          openItems: openItemsList,
          invoicesOnPayment,
        });
        if (!combinedData) {
          setLoading(false);
          setUploadingData(false);
          setMode('VIEW_DATA');
          dispatch(
            openMessageModal({
              modalType: 'ERROR',
              title: 'Error while uploading data',
              subTitle: 'Please try again',
              buttonText: 'Close',
            })
          );
          return;
        }
        await uploadChunks(combinedData.invoices, 'INVOICES', 2000);
        await uploadChunks(combinedData.payments, 'PAYMENTS', 2000);
        await uploadChunks(combinedData.creditMemos, 'CREDIT_MEMOS', 2000);
        await uploadChunks(combinedData.debitMemos, 'DEBIT_MEMOS', 2000);
        await uploadChunks(combinedData.financeCharges, 'FINANCE_CHARGES', 2000);
        await uploadChunks(combinedData.customers, 'CUSTOMERS', 2000);
        await uploadChunks(combinedData.jobs, 'JOBS', 2000);
        await uploadChunks(customerIds, 'PROVIDER_IDS', 2000);
      }
      // uploaded excel file;
      try {
        for (const file of uploadedFiles) {
          await firebaseLargeFileUploadHelper({
            blobUrl: URL.createObjectURL(file.file),
            fileNameWithExtension: file.fileName,
            storagePath: folderExcelName,
          });
        }
      } catch (err) {
        console.log(err);
      }

      const manualUploadedData = await dispatch(
        manualUploadData({
          folderPath: folderName,
          supplierId: currentSupplier?.id || '',
          folderExcelName,
        })
      );

      if (manualUploadedData.type === 'manualUpload/manualUploadData/fulfilled') {
        await dispatch(
          getManualUploadInvoices({ itemsPerPage: 50, supplierId: currentSupplier?.id || '' })
        );
        handleCloseModal();
        setLoading(false);
        setUploadingData(false);
        setMode('VIEW_DATA');
        if (
          !location.pathname.startsWith('/invoices') &&
          currentSupplier?.integrationType !== 'LIEN_ONLY_UPLOAD' &&
          currentSupplier?.integrationType !== 'P21_MANUAL_UPLOAD' &&
          currentSupplier?.integrationType !== 'DDI_MANUAL_UPLOAD' &&
          currentSupplier?.integrationType !== 'MARKS_MANUAL_UPLOAD' &&
          currentSupplier?.integrationType !== 'ECLIPSE_MANUAL_UPLOAD' &&
          currentSupplier?.integrationType !== 'EAGLE_MANUAL_UPLOAD'
        ) {
          setTriggerNavigationToInvoices({});
        }
      } else if (manualUploadedData.type === 'manualUpload/manualUploadData/rejected') {
        setLoading(false);
        setUploadingData(false);
        setMode('VIEW_DATA');
        const pp = manualUploadedData.payload as any;
        dispatch(
          openMessageModal({
            modalType: 'ERROR',
            title: 'Error while uploading data',
            subTitle: pp || '',
            buttonText: 'Close',
          })
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setUploadingData(false);
      setMode('VIEW_DATA');
      dispatch(
        openMessageModal({
          modalType: 'ERROR',
          title: 'Error while uploading data',
          subTitle: err as string,
          buttonText: 'Close',
        })
      );
    }
  };

  const handleConfirmImport = async () => {
    if (Object.values(generalCheck).some((item) => item === true)) {
      dispatch(
        openMessageModal({
          modalType: 'WARN',
          title: 'Some data is already in our system. ',
          subTitle: `${
            generalCheck.isSomePaymentsAlreadySaved
              ? 'Payments that were already saved will not be applied, '
              : ''
          } ${
            generalCheck.isSomeInvoicesHaveDillPaymentSaved
              ? 'Invoices that were already paid using Dill will not be updated, '
              : ''
          } Confirm that you would like to proceed`,
          buttonText: 'Close',
          isSecondButton: true,
          secondButtonText: 'Proceed',
          onSecondButtonClick: async () => {
            handleImportData();
          },
        })
      );
    } else {
      handleImportData();
    }
  };

  const getColumns = (tabId: string) => {
    if (tabId === 'invoice') {
      return columns.invoiceColumns;
    }
    if (tabId === 'lineItems') {
      return columns.lineItemsColumns;
    }
    if (tabId === 'job') {
      return columns.jobColumns;
    }
    if (tabId === 'customer') {
      return columns.customerColumns;
    }
    if (tabId === 'payments') {
      return columns.paymentsColumns;
    }
    if (tabId === 'openItems') {
      return columns.openItemsColumns;
    }
    if (tabId === 'invoiceLienOnly') {
      return columns.invoiceLienOnlyColumns;
    }
    if (tabId === 'jobLienOnly') {
      return columns.jobLienOnlyColumns;
    }
    if (tabId === 'customerLienOnly') {
      return columns.customerLienOnlyColumns;
    }
    if (tabId === 'p21ManualInvoices') {
      return columns.p21ManualInvoiceColumns;
    }
    if (tabId === 'p21ManualLineItems') {
      return columns.p21ManualLineItemsColumns;
    }
    if (tabId === 'p21ManualCustomers') {
      return columns.p21ManualCustomerColumns;
    }
    if (tabId === 'p21ManualOrders') {
      return columns.p21ManualOrderColumns;
    }
    if (tabId === 'ddiManualUploadInvoices') {
      return columns.ddiManualUploadInvoiceColumns;
    }
    if (tabId === 'ddiManualUploadLineItems') {
      return columns.ddiManualUploadLineItemsColumns;
    }
    if (tabId === 'ddiManualUploadCustomers') {
      return columns.ddiManualUploadCustomerColumns;
    }
    if (tabId === 'eclipseManualCustomers') {
      return columns.eclipseManualCustomerColumns;
    }
    if (tabId === 'eclipseManualInvoices') {
      return columns.eclipseManualInvoiceColumns;
    }
    if (tabId === 'eclipseManualOrders') {
      return columns.eclipseManualOrderColumns;
    }
    if (tabId === 'eagleManualCustomers') {
      return columns.eagleManualCustomerColumns;
    }
    if (tabId === 'eagleManualInvoices') {
      return columns.eagleManualInvoiceColumns;
    }
    if (tabId === 'eagleManualArDocuments') {
      return columns.eagleManualArDocumentColumns;
    }
    if (tabId === 'marksManualUploadInvoices') {
      return columns.marksManualUploadInvoiceColumns;
    }
    if (tabId === 'marksManualUploadLineItems') {
      return columns.marksManualUploadLineItemsColumns;
    }
    if (tabId === 'marksManualUploadCustomers') {
      return columns.marksManualUploadCustomerColumns;
    }
    if (tabId === 'marksManualUploadPayments') {
      return columns.marksManualUploadPaymentsColumns;
    }
    return [];
  };

  const getColumnTotalWidth = (tabId: string) => {
    return getColumns(tabId).reduce((curr, prev) => {
      return curr + prev.width;
    }, 0);
  };

  const selectedTabTableColumns = useMemo(() => {
    if (selectedTab) {
      return getColumns(selectedTab);
    }

    return [];
  }, [selectedTab]);

  const getTabData = (tabId: string) => {
    if (tabId === 'invoice') {
      return invoices;
    }
    if (tabId === 'lineItems') {
      return lineItemsList;
    }
    if (tabId === 'job') {
      return jobList;
    }
    if (tabId === 'customer') {
      return customerList;
    }
    if (tabId === 'payments') {
      return payments;
    }
    if (tabId === 'openItems') {
      return openItemsList;
    }
    if (tabId === 'invoiceLienOnly') {
      return [...invoicesLienOnlyError, ...invoicesLienOnly];
    }
    if (tabId === 'jobLienOnly') {
      return [...jobLienOnlyListError, ...jobLienOnlyList];
    }
    if (tabId === 'customerLienOnly') {
      return [...customerLienOnlyListError, ...customerLienOnlyList];
    }
    if (tabId === 'p21ManualInvoices') {
      return p21ManualInvoices;
    }
    if (tabId === 'p21ManualLineItems') {
      return p21ManualLineItemsList;
    }
    if (tabId === 'p21ManualCustomers') {
      return p21ManualCustomerList;
    }
    if (tabId === 'p21ManualOrders') {
      return p21ManualOrderList;
    }
    if (tabId === 'ddiManualUploadInvoices') {
      return ddiManualUploadInvoicesList;
    }
    if (tabId === 'ddiManualUploadLineItems') {
      return ddiManualUploadLineItemsList;
    }
    if (tabId === 'ddiManualUploadCustomers') {
      return ddiManualUploadCustomerList;
    }
    if (tabId === 'eclipseManualCustomers') {
      return eclipseManualCustomersList;
    }
    if (tabId === 'eclipseManualInvoices') {
      return eclipseManualInvoicesList;
    }
    if (tabId === 'eclipseManualOrders') {
      return eclipseManualOrdersList;
    }
    if (tabId === 'eagleManualCustomers') {
      return eagleManualCustomersList;
    }
    if (tabId === 'eagleManualInvoices') {
      return eagleManualInvoicesList;
    }
    if (tabId === 'eagleManualArDocuments') {
      return eagleManualArDocumentsList;
    }
    if (tabId === 'marksManualUploadInvoices') {
      return marksManualUploadInvoicesList;
    }
    if (tabId === 'marksManualUploadLineItems') {
      return marksManualUploadLineItemsList;
    }
    if (tabId === 'marksManualUploadCustomers') {
      return marksManualUploadCustomerList;
    }
    if (tabId === 'marksManualUploadPayments') {
      return marksManualUploadPaymentsList;
    }
    return [];
  };

  const getCurrentTabData = (tabId: string) => {
    if (tabId === 'invoice') {
      return invoices.slice(itemOffset, endOffset);
    }
    if (tabId === 'lineItems') {
      return lineItemsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'job') {
      return jobList.slice(itemOffset, endOffset);
    }
    if (tabId === 'customer') {
      return customerList.slice(itemOffset, endOffset);
    }
    if (tabId === 'payments') {
      return payments.slice(itemOffset, endOffset);
    }
    if (tabId === 'openItems') {
      return openItemsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'invoiceLienOnly') {
      return [...invoicesLienOnlyError, ...invoicesLienOnly].slice(itemOffset, endOffset);
    }
    if (tabId === 'jobLienOnly') {
      return [...jobLienOnlyListError, ...jobLienOnlyList].slice(itemOffset, endOffset);
    }
    if (tabId === 'customerLienOnly') {
      return [...customerLienOnlyListError, ...customerLienOnlyList].slice(itemOffset, endOffset);
    }
    if (tabId === 'p21ManualInvoices') {
      return p21ManualInvoices.slice(itemOffset, endOffset);
    }
    if (tabId === 'p21ManualLineItems') {
      return p21ManualLineItemsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'p21ManualCustomers') {
      return p21ManualCustomerList.slice(itemOffset, endOffset);
    }
    if (tabId === 'p21ManualOrders') {
      return p21ManualOrderList.slice(itemOffset, endOffset);
    }
    if (tabId === 'ddiManualUploadInvoices') {
      return ddiManualUploadInvoicesList.slice(itemOffset, endOffset);
    }
    if (tabId === 'ddiManualUploadLineItems') {
      return ddiManualUploadLineItemsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'ddiManualUploadCustomers') {
      return ddiManualUploadCustomerList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eclipseManualCustomers') {
      return eclipseManualCustomersList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eclipseManualInvoices') {
      return eclipseManualInvoicesList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eclipseManualOrders') {
      return eclipseManualOrdersList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eagleManualCustomers') {
      return eagleManualCustomersList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eagleManualInvoices') {
      return eagleManualInvoicesList.slice(itemOffset, endOffset);
    }
    if (tabId === 'eagleManualArDocuments') {
      return eagleManualArDocumentsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'marksManualUploadInvoices') {
      return marksManualUploadInvoicesList.slice(itemOffset, endOffset);
    }
    if (tabId === 'marksManualUploadLineItems') {
      return marksManualUploadLineItemsList.slice(itemOffset, endOffset);
    }
    if (tabId === 'marksManualUploadCustomers') {
      return marksManualUploadCustomerList.slice(itemOffset, endOffset);
    }
    if (tabId === 'marksManualUploadPayments') {
      return marksManualUploadPaymentsList.slice(itemOffset, endOffset);
    }
    return [];
  };

  const tabItems = useMemo(() => {
    const tabs: { id: string; name: string; icon: any }[] = [];
    if (getTabData('invoice').length > 0) {
      tabs.push({
        id: 'invoice',
        name: 'Invoice',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('lineItems').length > 0) {
      tabs.push({
        id: 'lineItems',
        name: 'Line Items',
        icon: <Notepad size={24} />,
      });
    }
    if (getTabData('customer').length > 0) {
      tabs.push({
        id: 'customer',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('job').length > 0) {
      tabs.push({
        id: 'job',
        name: 'Jobs',
        icon: <Briefcase size={24} />,
      });
    }
    if (getTabData('payments').length > 0) {
      tabs.push({
        id: 'payments',
        name: 'Payments',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('openItems').length > 0) {
      tabs.push({
        id: 'openItems',
        name: 'Other Open Items',
        icon: <BookOpen size={24} />,
      });
    }
    if (
      getTabData('invoiceLienOnly').length > 0 ||
      (getTabData('invoiceLienOnly').length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'INVOICE') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD')
    ) {
      tabs.push({
        id: 'invoiceLienOnly',
        name: 'Invoice',
        icon: <Notebook size={24} />,
      });
    }
    if (
      getTabData('customerLienOnly').length > 0 ||
      (getTabData('customerLienOnly').length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'CUSTOMER') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD')
    ) {
      tabs.push({
        id: 'customerLienOnly',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (
      getTabData('jobLienOnly').length > 0 ||
      (getTabData('jobLienOnly').length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'JOB') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD')
    ) {
      tabs.push({
        id: 'jobLienOnly',
        name: 'Jobs',
        icon: <Briefcase size={24} />,
      });
    }
    if (getTabData('p21ManualInvoices').length > 0) {
      tabs.push({
        id: 'p21ManualInvoices',
        name: 'Invoice',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('p21ManualLineItems').length > 0) {
      tabs.push({
        id: 'p21ManualLineItems',
        name: 'Line Items',
        icon: <Notepad size={24} />,
      });
    }
    if (getTabData('p21ManualCustomers').length > 0) {
      tabs.push({
        id: 'p21ManualCustomers',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('p21ManualOrders').length > 0) {
      tabs.push({
        id: 'p21ManualOrders',
        name: 'Orders',
        icon: <ShoppingBag size={22} />,
      });
    }
    if (
      getTabData('ddiManualUploadInvoices').length > 0 &&
      currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD'
    ) {
      tabs.push({
        id: 'ddiManualUploadInvoices',
        name: 'Invoice',
        icon: <Notebook size={24} />,
      });
    }
    if (
      getTabData('ddiManualUploadLineItems').length > 0 &&
      currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD'
    ) {
      tabs.push({
        id: 'ddiManualUploadLineItems',
        name: 'Line Items',
        icon: <Notepad size={24} />,
      });
    }
    if (
      getTabData('ddiManualUploadCustomers').length > 0 &&
      currentSupplier?.integrationType === 'DDI_MANUAL_UPLOAD'
    ) {
      tabs.push({
        id: 'ddiManualUploadCustomers',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('marksManualUploadInvoices').length > 0) {
      tabs.push({
        id: 'marksManualUploadInvoices',
        name: 'Invoice',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('marksManualUploadLineItems').length > 0) {
      tabs.push({
        id: 'marksManualUploadLineItems',
        name: 'Line Items',
        icon: <Notepad size={24} />,
      });
    }
    if (getTabData('marksManualUploadCustomers').length > 0) {
      tabs.push({
        id: 'marksManualUploadCustomers',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('marksManualUploadPayments').length > 0) {
      tabs.push({
        id: 'marksManualUploadPayments',
        name: 'Payments',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('eclipseManualCustomers').length > 0) {
      tabs.push({
        id: 'eclipseManualCustomers',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('eclipseManualInvoices').length > 0) {
      tabs.push({
        id: 'eclipseManualInvoices',
        name: 'Invoices',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('eclipseManualOrders').length > 0) {
      tabs.push({
        id: 'eclipseManualOrders',
        name: 'Orders',
        icon: <ShoppingBag size={22} />,
      });
    }
    if (getTabData('eagleManualCustomers').length > 0) {
      tabs.push({
        id: 'eagleManualCustomers',
        name: 'Customer',
        icon: <AddressBook size={24} />,
      });
    }
    if (getTabData('eagleManualInvoices').length > 0) {
      tabs.push({
        id: 'eagleManualInvoices',
        name: 'Invoices',
        icon: <Notebook size={24} />,
      });
    }
    if (getTabData('eagleManualArDocuments').length > 0) {
      tabs.push({
        id: 'eagleManualArDocuments',
        name: 'A/R Documents',
        icon: <ShoppingBag size={22} />,
      });
    }
    if (tabs.length > 0) {
      setSelectedTab(tabs[0].id as any);
    }
    return tabs;
  }, [
    open,
    payments,
    customerList,
    jobList,
    lineItemsList,
    invoices,
    openItemsList,
    invoicesLienOnly,
    jobLienOnlyList,
    customerLienOnlyList,
    p21ManualInvoices,
    p21ManualLineItemsList,
    p21ManualCustomerList,
    p21ManualOrderList,
    ddiManualUploadInvoicesList,
    ddiManualUploadLineItemsList,
    ddiManualUploadCustomerList,
    marksManualUploadInvoicesList,
    marksManualUploadLineItemsList,
    marksManualUploadCustomerList,
    eclipseManualInvoicesList,
    eclipseManualOrdersList,
    eclipseManualCustomersList,
    eagleManualInvoicesList,
    eagleManualArDocumentsList,
    eagleManualCustomersList,
  ]);

  const selectedTabTableData = useMemo(() => {
    if (selectedTab) {
      return getTabData(selectedTab);
    }

    return [];
  }, [
    selectedTab,
    payments,
    customerList,
    jobList,
    lineItemsList,
    invoices,
    openItemsList,
    invoicesLienOnly,
    jobLienOnlyList,
    customerLienOnlyList,
    p21ManualInvoices,
    p21ManualLineItemsList,
    p21ManualCustomerList,
    p21ManualOrderList,
    ddiManualUploadInvoicesList,
    ddiManualUploadLineItemsList,
    ddiManualUploadCustomerList,
    marksManualUploadInvoicesList,
    marksManualUploadLineItemsList,
    marksManualUploadCustomerList,
    eclipseManualInvoicesList,
    eclipseManualOrdersList,
    eclipseManualCustomersList,
    eagleManualInvoicesList,
    eagleManualArDocumentsList,
    eagleManualCustomersList,
  ]);

  const hasErrorsIds = useMemo(() => {
    const errorResult: string[] = [];
    if (invoicesLienOnlyError.length > 0) {
      errorResult.push('invoice does not have invoice number or has invalid date');
    }
    if (jobLienOnlyListError.length > 0) {
      errorResult.push('job does not have id');
    }
    if (customerLienOnlyListError.length > 0) {
      if (
        customerLienOnlyListError.some((customer) => customer.name === '') &&
        customerLienOnlyListError.some((customer) => customer.id === '')
      ) {
        errorResult.push('customer name and id is empty');
      } else if (
        customerLienOnlyListError.some((customer) => customer.id === '') &&
        customerLienOnlyListError.some((customer) => customer.name !== '')
      ) {
        errorResult.push('customer id is empty');
      } else if (
        customerLienOnlyListError.some((customer) => customer.id !== '') &&
        customerLienOnlyListError.some((customer) => customer.name === '')
      ) {
        errorResult.push('customer name is empty');
      }
    }

    return errorResult;
  }, [invoicesLienOnlyError, jobLienOnlyListError, customerLienOnlyListError]);

  // Pagination function
  const pageCount = Math.ceil(selectedTabTableData.length / itemsPerPage);

  const handlePageClick = (event: any) => {
    setCurrentPage(event.selected + 1);
    const newOffset = (event.selected * itemsPerPage) % selectedTabTableData.length;
    setItemOffset(newOffset);
  };

  const cummulativeOffset = useMemo(() => {
    let result = 0;
    const currentTotalItems = endOffset / itemsPerPage;
    if (pageCount === currentTotalItems) {
      result = selectedTabTableData.length;
    } else if (selectedTabTableData.length === 0) {
      result = 0;
    } else {
      const itemsTotal = currentTotalItems * itemsPerPage;
      result = itemsTotal;
    }
    return result;
  }, [endOffset, selectedTabTableData]);
  const currentItems = selectedTabTableData.slice(itemOffset, endOffset);

  const curentUploadFileProgres = useMemo(() => {
    if (curentFileUploaded?.size) {
      if (curentFileUploaded?.size > 1000000) {
        const uploadedSize = (curentFileUploaded?.size * uploadProgress) / 100000000;
        const currentFileSize = curentFileUploaded?.size / 1000000;
        return `${uploadedSize.toFixed(2)}MB from ${currentFileSize.toFixed(2)} MB`;
      } else {
        const uploadedSize = (curentFileUploaded?.size * uploadProgress) / 100000;
        const currentFileSize = curentFileUploaded?.size / 1000;
        return `${uploadedSize.toFixed(2)}KB from ${currentFileSize.toFixed(2)}KB`;
      }
    }
    return '';
  }, [uploadProgress, curentFileUploaded]);

  const noDataFoundError = useMemo(() => {
    const listFilesNotFound = [];
    if (mode === 'VIEW_DATA') {
      if (
        invoicesLienOnly.length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'INVOICE') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD'
      ) {
        listFilesNotFound.push('invoice');
      }
      if (
        customerLienOnlyList.length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'CUSTOMER') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD'
      ) {
        listFilesNotFound.push('customer');
      }
      if (
        jobLienOnlyList.length === 0 &&
        uploadedFiles.some((file: any) => file.fileType === 'JOB') &&
        currentSupplier?.integrationType === 'LIEN_ONLY_UPLOAD'
      ) {
        listFilesNotFound.push('jobs');
      }
    }
    return listFilesNotFound;
  }, [mode, invoicesLienOnly, jobLienOnlyList, customerLienOnlyList]);

  const handleCheckInvoiceInvoiceProperties = async () => {
    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: 'Confirm File Import!',
        subTitle: `${
          uploadedFiles.length > 1 ? 'One file' : 'The file'
        } uploaded has been identified as a job file but contains some properties that are for invoice files. Confirm that you would like to proceed with the import as a job file.`,
        buttonText: 'Close',
        isSecondButton: true,
        secondButtonText: 'Proceed',
        onSecondButtonClick: async () => {
          handleProcessData();
        },
      })
    );
  };

  const renderRow = (item: { id: string; content: React.ReactNode }) => {
    const index = Number(item.id);
    const data = getCurrentTabData(selectedTab)[index];
    return (
      <div
        key={index + 'oo' + `${item.id}`}
        className={`w-full flex h-full px-4 justify-between items-center ${
          index % 2 === 0 ? 'GREY_50-BG' : ''
        }`}>
        {getColumns(selectedTab).map((column, columnIndex) => {
          type ObjectKey = keyof typeof data;
          const columnKey = column.id as ObjectKey;
          let value: any = data[columnKey];
          if (
            column.id === 'subTotal' ||
            column.id === 'totalAmount' ||
            column.id === 'unitAmount' ||
            column.id === 'taxAmount' ||
            column.id === 'discountAmount' ||
            column.id === 'amount' ||
            column.id === 'balance' ||
            column.id === 'cashAmount' ||
            column.id.includes('totalSalesAmt') ||
            column.id === 'appliedOrderCashAmt'
          ) {
            const amount = data[columnKey];
            if (amount) {
              if (isNaN(Number(amount))) {
                value = `${Number(amount)}`;
              } else {
                if (typeof amount === 'string' || typeof amount === 'number') {
                  value = formatMoney(amount ?? '', 2);
                }
              }
            } else {
              value = '';
            }
          }
          if (column.id === 'invoiceDate') {
            value = moment(data[columnKey]).format('MM/DD/YYYY');
          }
          if (column.id === 'shipDate') {
            value = moment(data[columnKey]).format('MM/DD/YYYY');
          }
          if (column.id === 'docDate') {
            value = moment(data[columnKey]).format('MM/DD/YYYY');
          }
          if (column.id === 'dueDate') {
            value = moment(data[columnKey]).format('MM/DD/YYYY');
          }
          if (column.id === 'orderDate') {
            value = moment(data[columnKey]).format('MM/DD/YYYY');
          }
          if (column.id === 'discountDate' || column.id === 'paymentDate') {
            value = data[columnKey] ? moment(data[columnKey]).format('MM/DD/YYYY') : '';
          }
          let corruptData = isDataCorrupt(data) || data?.id === '';
          if (column.id === 'name' && selectedTab === 'customerLienOnly') {
            if (data[columnKey] == '') {
              corruptData = true;
            }
          }
          if (column.id === 'invoiceDueDate') {
            value = data[columnKey] ? moment(data[columnKey]).format('MM/DD/YYYY') : '';
          }
          return (
            <div
              key={column.id}
              style={{ flex: column.width / getColumnTotalWidth(selectedTab) }}
              className={twMerge(`py-1 px-1 h-full overflow-y-hidden flex items-center`)}>
              <div className="whitespace-break-spaces w-fit">
                {column.cellType === 'VIEW_TEXT' && columnIndex === 0 && (
                  <div className="flex">
                    <ReactTooltip
                      id={`manualUpload-${index}`}
                      place="top"
                      variant="dark"
                      opacity={'100%'}
                      style={{
                        display: 'flex',
                        background: '#334155',
                        width: '300px',
                        zIndex: '70',
                        borderRadius: '8px',
                        alignItems: 'center',
                        justifyItems: 'center',
                        fontWeight: '600',
                      }}></ReactTooltip>
                    <ReactTooltip
                      id={`balanceiszero-${index}`}
                      place="top"
                      variant="dark"
                      opacity={'100%'}
                      style={{
                        display: 'flex',
                        background: '#334155',
                        width: '300px',
                        zIndex: '70',
                        borderRadius: '8px',
                        alignItems: 'center',
                        justifyItems: 'center',
                        fontWeight: '600',
                      }}>
                      <span>{`The payment posted is greater than balance so we will update the balance to 0`}</span>
                    </ReactTooltip>
                    <ReactTooltip
                      id={`data-corrupt-${index}`}
                      place="top"
                      variant="dark"
                      opacity={'100%'}
                      style={{
                        display: 'flex',
                        background: '#334155',
                        width: '300px',
                        zIndex: '70',
                        borderRadius: '8px',
                        alignItems: 'center',
                        justifyItems: 'center',
                        fontWeight: '600',
                      }}>
                      <span>{`This row contains data that is not in the correct format. Therefore, it will not be uploaded`}</span>
                    </ReactTooltip>
                    {corruptData && (
                      <Warning
                        size={20}
                        weight="bold"
                        className="ERROR_100-BG ERROR_500-CLR mr-1"
                        data-tooltip-id={`data-corrupt-${index}`}
                      />
                    )}
                    <div className="text-xs word-wrap">{value}</div>
                  </div>
                )}
                {column.cellType === 'VIEW_TEXT' && columnIndex !== 0 && (
                  <div className="text-xs word-wrap">{value}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEmptyTable = () => {
    const findTab = tabItems.find((tab) => tab.id === selectedTab);
    return (
      <div className="WHITE-BG">
        <div className="flex justify-center items-center h-[300px]">
          <div className="text-sm GREY_500-CLR">
            {`No data found for ${
              findTab?.name.toLowerCase() ?? ''
            } please check the headers of the ${findTab?.name.toLowerCase() ?? ''} file`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div>
        <div
          className={`flex min-h-72 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col ${
            mode === 'VIEW_DATA' ? ' w-[900px]' : ' w-[540px]'
          }`}>
          <h2 className="text-lg font-semibold mx-4 my-3">Import CSV</h2>
          <X
            className="absolute right-4 top-4 cursor-pointer"
            color={COLORS.GREY_500}
            onClick={handleCloseModal}
          />
          <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400"></div>
          {mode === 'WAITING FILE' && (
            <div className="mx-4 mt-3 mb-6">
              <div {...getRootProps({ style })} className="flex flex-col">
                <input {...getInputProps()} />
                <p>Choose files or drag 'n' drop some files here</p>
                <div className="text-sm mt-2 TEXT_SECONDARY-CLR">Accepted file .csv .xslx .xls</div>

                <DillButton
                  label="Browse Files"
                  variant="secondary"
                  onClick={openFilePicker}
                  className="mt-2"
                />
              </div>
              {uploadedFiles.length > 0 && (
                <div className="border GREY_200-BORDER rounded p-2 mt-2">
                  {uploadedFiles.map((uploadedFile, i) => (
                    <div key={`uploaded_files_${i}`} className="flex items-center my-1">
                      <div className="w-[22px]">
                        <CheckCircle color={COLORS.SUCCESS_500} size={20} weight="fill" />
                      </div>
                      <div className="capitalize w-[180px] text-sm ml-3">
                        {`${uploadedFile?.fileType?.replaceAll('_', ' ')?.toLowerCase()} file:`}
                      </div>
                      <div>
                        <div className="text-sm">{uploadedFile.fileName}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {missingFiles.length > 0 && (
                <div>
                  <div className="border ERROR_500-BORDER rounded p-2 mt-2">
                    {missingFiles.map((missingFile, i) => (
                      <div key={`missing_files_${i}`} className="flex items-center my-1">
                        <XCircle color={COLORS.ERROR_500} size={20} weight="fill" />
                        <div className="capitalize text-sm ml-2">
                          {`${missingFile?.toLowerCase()} file missing`}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm ERROR_500-CLR mt-2">{`Missing file${
                    missingFiles.length > 1 ? 's' : ''
                  } above`}</div>
                </div>
              )}
              {fileErrors.map((fileError, i) => (
                <div key={`file_error_${i}`} className="flex mt-2">
                  <div className="text-sm">{`${
                    fileError.fileName ? `${fileError.fileName}:` : ''
                  } `}</div>
                  <div className="text-sm ERROR_500-CLR">{fileError.errorMessage}</div>
                </div>
              ))}
              {error && <div className="my-2 text-xs ERROR_500-CLR text-center">{error}</div>}
            </div>
          )}
          {mode === 'UPLOADING' && (
            <div className="flex m-4 p-4 outline-dashed rounded outline-2 outline-offset-0 outline-gray-400 items-center flex-col">
              <div
                className="flex PRIMARY_200-BG rounded-full items-center justify-center mb-3"
                style={{
                  width: 40,
                  height: 40,
                  animation: 'spin 2s linear infinite',
                }}>
                <Loader size={24} color={COLORS.PRIMARY_500} />
              </div>
              <div>Please wait your file is uploading. . .</div>
              <div className="w-full border GREY_200-BORDER p-2 mt-2">
                <div className="flex">
                  <div className="w-[46px]">
                    <FileCsv size={44} />
                  </div>
                  <div className="ml-2">
                    <div className="text-sm">{curentFileUploaded?.name}</div>
                    <div className="text-sm">{curentUploadFileProgres}</div>
                  </div>
                </div>
                <div className="mt-2 rounded-full GREY_300-BG w-full h-[8px]">
                  <div
                    className={`mt-2 rounded-full PRIMARY_500-BG w-${count}/${fileNumber} h-[8px]`}
                  />
                </div>
              </div>
            </div>
          )}
          {mode === 'PROCESSING' && (
            <div className="flex m-4 p-4 outline-dashed rounded outline-2 outline-offset-0 outline-gray-400 items-center flex-col">
              <div
                className="flex PRIMARY_200-BG rounded-full items-center justify-center mb-3"
                style={{
                  width: 100,
                  height: 100,
                  animation: 'spin 2s linear infinite',
                }}>
                <Loader size={64} color={COLORS.PRIMARY_500} />
              </div>
              <div className="mt-4">{`${
                uploadingData
                  ? `Please wait your file${
                      uploadedFiles.length > 1 ? 's are' : ' is'
                    } being uploaded . . .`
                  : 'Processing data. . .'
              }`}</div>
            </div>
          )}
          {mode === 'VIEW_DATA' && (
            <div className="PRIMARY_50-BG my-2 mx-4 ">
              <div className="GREY_300-BORDER border-2 w-fit rounded-xl overflow-hidden">
                <div className="flex">
                  {tabItems.map((tabItem, index) => {
                    const idKey = tabItem.id as
                      | 'invoice'
                      | 'job'
                      | 'lineItems'
                      | 'customer'
                      | 'payments'
                      | 'openItems';
                    return (
                      <div
                        key={index + 'oo'}
                        onClick={() => {
                          setSelectedTab(idKey);
                          setItemOffset(0);
                          setCurrentPage(0);
                          setRefreshScroll((prev) => !prev);
                        }}
                        className={twMerge(
                          `flex cursor-pointer px-4 py-2 items-center`,
                          selectedTab === idKey ? 'PRIMARY_500-CLR WHITE-BG' : 'GREY_500-CLR ',
                          index < tabItems.length - 1 ? 'border-r-2' : ''
                        )}>
                        {tabItem.icon}
                        <div className="ml-2 font-semibold">{tabItem.name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4 GREY_300-BORDER border rounded-xl overflow-scroll">
                {selectedTabTableColumns && (
                  <div className="flex border-b px-5 items-center w-full sticky top-0 PRIMARY_50-BG py-3 z-20">
                    {selectedTabTableColumns.map((column) => {
                      return (
                        <div
                          key={column.id}
                          style={{ flex: column.width / getColumnTotalWidth(selectedTab) }}
                          className="flex w-full">
                          <p className="TEXT_SECONDARY-CLR text-xs w-full text-left">
                            {column.name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {selectedTab && currentItems.length > 0 && (
                  <div className=" WHITE-BG">
                    <AppVirtualizedList
                      itemsLength={currentItems.length}
                      itemHeight={45}
                      containerHeight={30}
                      renderRow={renderRow}
                      className="pb-[40px]"
                      refreshScroll={refreshScroll}
                    />
                  </div>
                )}
                {selectedTab && currentItems.length === 0 && renderEmptyTable()}
              </div>
              {/* Pagination component */}
              <div className="flex flex-row justify-between items-center w-full mt-3 mb-2 ml-1">
                <div className="text-xs GREY_500-CLR">{`Showing ${
                  selectedTabTableData.length !== 0 ? `${itemOffset + 1} to` : ''
                } ${cummulativeOffset} from ${selectedTabTableData.length} ${getTabName(
                  selectedTab
                )}${selectedTabTableData.length > 1 ? 's' : ''}`}</div>
                <ReactPaginate
                  breakLabel="..."
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={2}
                  pageCount={pageCount}
                  forcePage={Math.min(Math.max(0, currentPage - 1), pageCount - 1)}
                  previousLabel={<CaretLeft size={20} />}
                  nextLabel={<CaretRight size={20} />}
                  renderOnZeroPageCount={null}
                  containerClassName="pagination"
                  activeClassName="active"
                />
              </div>
            </div>
          )}
          <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400 mt-4 mb-2"></div>
          {hasErrorsIds.length > 0 && mode === 'VIEW_DATA' && (
            <p className="mx-3 ERROR_500-CLR mb-2 text-xs text-center">{`There are errors in the data for: ${hasErrorsIds.join(
              ', '
            )}.`}</p>
          )}
          {noDataFoundError.length > 0 && mode === 'VIEW_DATA' && (
            <p className="mx-3 ERROR_500-CLR mb-2 text-xs text-center">{`No data was found for the following files: ${noDataFoundError.join(
              ', '
            )}.`}</p>
          )}
          <div className="flex justify-end px-4 pb-2 gap-2">
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => {
                handleCloseModal();
              }}
            />
            {mode !== 'UPLOADING' && (
              <DillButton
                label={mode === 'WAITING FILE' ? 'Process Files' : 'Import'}
                isLoading={loading}
                isDisabled={loading}
                onClick={() => {
                  const missingFileError = checkErrors();
                  if (missingFileError.length === 0 && mode === 'WAITING FILE') {
                    if (hasInvoiceProperties) {
                      handleCheckInvoiceInvoiceProperties();
                    } else {
                      handleProcessData();
                    }
                  } else if (mode === 'VIEW_DATA') {
                    handleConfirmImport();
                  }
                }}
                width={100}
                className="flex justify-center"
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
